import { TotalResults } from '@shared/components/ui/TotalResults';
import { FilterConditionOutput } from '@components/ui/AdvancedFilter';
import { usePageNavigation } from '@shared/components/navigation/usePageNavigation';
import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useReusedMain } from '../../hooks/useReusedMain';
import { CardList, ReusedAgGridTable } from '../display';
import { ErrorState, LoadingState } from '../feedback';
import { FiltersSection } from '../interaction';
import { generateReusedLabel } from '@shared/utils/reusedLabelGenerator';

export const Main = () => {
  const { t } = useTranslation('reusedModule');

  // Breadcrumbs y título de página
  const breadcrumbs = useMemo(() => [{ label: t('breadcrumb.reused', 'Rehusados') }], [t]);

  usePageNavigation({ breadcrumbs });

  // Estado para controlar que el spinner de carga inicial se muestre solo una vez
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Hook useQueryParams para sincronizar con URL
  const { params: queryParams, updateParams: updateQueryParams } = useQueryParams({
    search: { type: 'string', default: '' },
    filters: { type: 'json', default: [] as FilterConditionOutput[] },
    sortBy: { type: 'string', default: 'id' },
    sortDirection: { type: 'string', default: 'desc' },
  });

  // Estados locales sincronizados con URL
  const [searchTerm, setSearchTerm] = useState(queryParams.search ?? '');
  const [filterConditions, setFilterConditions] = useState<FilterConditionOutput[]>(
    Array.isArray(queryParams.filters) ? queryParams.filters : [],
  );
  const [sorting, setSorting] = useState<any>(() => ({
    field: queryParams.sortBy || 'id',
    direction: (queryParams.sortDirection as 'asc' | 'desc') || 'desc',
  }));

  // Efectos para sincronizar estados locales con URL
  useEffect(() => {
    setSearchTerm(queryParams.search ?? '');
  }, [queryParams.search]);

  useEffect(() => {
    setFilterConditions(Array.isArray(queryParams.filters) ? queryParams.filters : []);
  }, [queryParams.filters]);

  useEffect(() => {
    const nextSorting = {
      field: queryParams.sortBy || 'id',
      direction: (queryParams.sortDirection as 'asc' | 'desc') || 'desc',
    };
    setSorting((prev: any) => {
      if (prev && prev.field === nextSorting.field && prev.direction === nextSorting.direction) {
        return prev;
      }
      return nextSorting;
    });
  }, [queryParams.sortBy, queryParams.sortDirection]);

  // Handlers optimizados para evitar bucles infinitos
  const handleSearch = useCallback(
    (searchValue: string) => {
      setSearchTerm(searchValue);
      if (queryParams.search !== searchValue) {
        updateQueryParams({ search: searchValue });
      }
    },
    [updateQueryParams, queryParams.search],
  );

  const handleFilterChange = useCallback(
    (conditions: FilterConditionOutput[]) => {
      setFilterConditions(conditions);
      const currentFilters = Array.isArray(queryParams.filters) ? queryParams.filters : [];
      if (JSON.stringify(currentFilters) !== JSON.stringify(conditions)) {
        updateQueryParams({ filters: conditions });
      }
    },
    [updateQueryParams, queryParams.filters],
  );

  const handleSortingChange = useCallback(
    (newSorting: any) => {
      setSorting(newSorting);
      const sortBy = newSorting?.field || 'id';
      const sortDirection = newSorting?.direction || 'desc';

      if (queryParams.sortBy !== sortBy || queryParams.sortDirection !== sortDirection) {
        updateQueryParams({
          sortBy: sortBy === 'id' ? undefined : sortBy,
          sortDirection: sortDirection === 'desc' ? undefined : sortDirection,
        });
      }
    },
    [updateQueryParams, queryParams.sortBy, queryParams.sortDirection],
  );

  // Hook que maneja toda la lógica de negocio
  const {
    // Datos
    reused,
    isLoading,
    isFetching,
    error,
    total,

    // Props para componentes
    reusedViewProps,

    // Handlers
    refetch,

    // Props para infinite scroll
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,

    // Estado de filtros
    hasActiveFilters,
  } = useReusedMain(searchTerm, filterConditions, sorting);

  // Efecto para desactivar el spinner de carga inicial después del primer fetch
  useEffect(() => {
    if (!isLoading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);

  const navigate = useNavigate();

  const renderReused = () => {
    if (isMobile) {
      return (
        <CardList
          reused={reused}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          total={total}
          onView={(item) => navigate(`/reused/${item.id}/receive`)}
          onPrint={async (item) => {
            const labelUrl = await generateReusedLabel({
              code: item.code,
              date: new Date(item.created_at.replace(' ', 'T')).toLocaleDateString('es-ES'),
            });
            window.open(labelUrl, '_blank');
          }}
        />
      );
    }

    // Usar AG Grid para la vista de escritorio
    return (
      <ReusedAgGridTable
        {...reusedViewProps}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        sorting={sorting}
        onSortingChange={handleSortingChange}
      />
    );
  };

  // Muestra el spinner SOLO en la carga inicial
  if (isInitialLoad) {
    return <LoadingState message={t('loading.message', 'Cargando recepciones...')} />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={refetch} />;
  }

  return (
    <div className="px-6 pt-4">
      {/* Header con filtros */}
      <div className="mb-4">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-3 flex-wrap">
            <FiltersSection handleSearch={handleSearch} onFilterChange={handleFilterChange} />
          </div>
          <TotalResults total={total} />
        </div>
      </div>

      {/* CRÍTICO: AG Grid siempre montado en el DOM - usar opacity en lugar de renderizado condicional */}
      <div className="relative">
        {/* Tabla - Siempre renderizada, controlada por opacity */}
        <div
          className={`transition-opacity duration-300 mb-4 mt-4 ${
            !isLoading && total > 0
              ? isFetching && !isFetchingNextPage
                ? 'opacity-50 pointer-events-none'
                : 'opacity-100'
              : 'opacity-0 pointer-events-none'
          }`}
        >
          {renderReused()}
        </div>

        {/* EmptyView superpuesto cuando no hay datos */}
        {!isLoading && reused.length === 0 && (
          <div className="absolute inset-0">
            <EmptyView type={hasActiveFilters ? 'noResults' : 'emptyState'} icon={EMPTY_VIEW_ICONS.RETURNED} />
          </div>
        )}
      </div>
    </div>
  );
};
